import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import { ClipLoader } from 'react-spinners';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/formatters';

function MonthlyReport() {
  const [transactions, setTransactions] = useState([]);
  const [prevTransactions, setPrevTransactions] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchMonthlyTransactions = useCallback(async (month) => {
    if (!month) return;
    setIsLoading(true);
    const firstDay = startOfMonth(month);
    const lastDay = endOfMonth(month);
    const prevMonth = subMonths(month, 1);
    const prevFirst = startOfMonth(prevMonth);
    const prevLast = endOfMonth(prevMonth);
    try {
      const [currentRes, prevRes] = await Promise.all([
        api.get(`/transactions/?${new URLSearchParams({ start_date: format(firstDay, 'y-MM-dd'), end_date: format(lastDay, 'y-MM-dd') })}`),
        api.get(`/transactions/?${new URLSearchParams({ start_date: format(prevFirst, 'y-MM-dd'), end_date: format(prevLast, 'y-MM-dd') })}`)
      ]);
      setTransactions(currentRes.data);
      setPrevTransactions(prevRes.data);
    } catch (error) {
      toast.error("Erro ao buscar transações do mês.");
      console.error("Erro ao buscar transações do mês", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMonthlyTransactions(selectedMonth);
  }, [selectedMonth, fetchMonthlyTransactions]);

  const summary = useMemo(() => {
    return transactions.reduce((acc, transaction) => {
      if (transaction.type === 'INCOME') {
        acc.income += transaction.amount;
      } else {
        acc.expense += transaction.amount;
      }
      return acc;
    }, { income: 0, expense: 0 });
  }, [transactions]);

  const totalBalance = summary.income - summary.expense;

  const prediction = useMemo(() => {
    const prevSummary = prevTransactions.reduce((acc, t) => {
      if (t.type === 'INCOME') acc.income += t.amount;
      else acc.expense += t.amount;
      return acc;
    }, { income: 0, expense: 0 });
    const prevBalance = prevSummary.income - prevSummary.expense;
    const diff = totalBalance - prevBalance;
    return { diff, prevBalance };
  }, [prevTransactions, totalBalance]);

  const handleMonthSelect = (date) => {
    if (date) {
      setSelectedMonth(date);
    }
    setIsPickerOpen(false);
  };

  const handleExport = () => {
    const toastId = toast.loading('Gerando relatório...');
    try {
      const header = ["Data", "Título", "Tipo", "Categoria", "Status", "Valor (R$)"];

      const dataToExport = transactions.map(t => [
        format(new Date(t.date), 'dd/MM/yyyy', { timeZone: 'UTC' }),
        t.title,
        t.type === 'INCOME' ? 'Receita' : 'Despesa',
        t.category?.name || 'Sem Categoria',
        t.status === 'CONFIRMED' ? 'Confirmado' : 'Pendente',
        t.amount
      ]);

      const summaryRows = [
          [],
          ["", "", "", "", "Total Receitas:", summary.income],
          ["", "", "", "", "Total Despesas:", summary.expense],
          ["", "", "", "", "Balanço do Mês:", totalBalance]
      ];

      const ws = XLSX.utils.aoa_to_sheet([header, ...dataToExport, ...summaryRows]);

      ws['!cols'] = [ {wch:12}, {wch:40}, {wch:10}, {wch:15}, {wch:12}, {wch:15} ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "RelatorioMensal");

      const fileName = `Relatorio_${format(selectedMonth, 'MM-yyyy')}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.dismiss(toastId);
      toast.success('Relatório gerado com sucesso!');
    } catch (error) {
        toast.dismiss(toastId);
        toast.error('Ocorreu um erro ao gerar o relatório.');
    }
  };

  const footer = (<p style={{textAlign: 'center', fontSize: '0.9rem', color: 'var(--text-secondary)'}}>Selecione um dia qualquer do mês desejado.</p>);

  return (
    <>
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-6">
        <div className="space-y-1">
          <h2 className="text-3xl font-bold font-manrope tracking-tight text-on-surface">Monthly Report</h2>
          <p className="text-secondary text-sm font-medium">A precise architectural view of your financial health for the current cycle.</p>
        </div>
        
        <div className="flex items-center space-x-3 relative">
          <button 
            type="button" 
            className="flex items-center space-x-2 px-4 py-2 bg-slate-50 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-100 border border-slate-200 transition-colors" 
            onClick={() => setIsPickerOpen(!isPickerOpen)}
          >
            <span>{format(selectedMonth, 'MMMM yyyy', { locale: ptBR })}</span>
            <span className="material-symbols-outlined text-sm">keyboard_arrow_down</span>
          </button>

          {isPickerOpen && (
            <div className="absolute top-12 right-32 z-50 bg-white shadow-xl rounded-xl border border-slate-200 overflow-hidden">
                <DayPicker
                    mode="single"
                    month={selectedMonth}
                    onMonthChange={setSelectedMonth}
                    onSelect={handleMonthSelect}
                    locale={ptBR}
                    footer={footer}
                />
            </div>
          )}

          <button 
            className="flex items-center space-x-2 px-4 py-2 bg-white text-primary text-sm font-bold rounded-lg hover:bg-slate-50 border border-slate-200 shadow-sm transition-colors disabled:opacity-50" 
            onClick={handleExport} 
            disabled={transactions.length === 0}
          >
            <span className="material-symbols-outlined text-sm">download</span>
            <span>Export to Excel</span>
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
            <ClipLoader color="#3525cd" size={50} />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm flex flex-col justify-between">
              <div>
                <p className="text-xs font-bold text-slate-500 tracking-wider uppercase mb-2">Total Income</p>
                <div className="flex justify-between items-center">
                  <h3 className="text-3xl font-bold font-manrope text-slate-900">{formatCurrency(summary.income)}</h3>
                  <span className="material-symbols-outlined text-slate-200 text-5xl">trending_up</span>
                </div>
              </div>
              <p className="text-sm font-semibold text-primary mt-6 flex items-center"><span className="material-symbols-outlined text-sm mr-1">arrow_upward</span> +12.4% vs last month</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm flex flex-col justify-between">
              <div>
                <p className="text-xs font-bold text-slate-500 tracking-wider uppercase mb-2">Total Expenses</p>
                <div className="flex justify-between items-center">
                  <h3 className="text-3xl font-bold font-manrope text-slate-900">{formatCurrency(summary.expense)}</h3>
                  <span className="material-symbols-outlined text-slate-200 text-5xl">trending_down</span>
                </div>
              </div>
              <p className="text-sm font-semibold text-error mt-6 flex items-center"><span className="material-symbols-outlined text-sm mr-1">arrow_upward</span> +4.2% higher burn rate</p>
            </div>
            
            <div className="bg-primary p-6 rounded-xl shadow-md flex flex-col justify-between text-white">
              <div>
                <p className="text-xs font-bold text-indigo-200 tracking-wider uppercase mb-2">Net Balance</p>
                <h3 className="text-3xl font-bold font-manrope">{formatCurrency(totalBalance)}</h3>
              </div>
              <p className="text-sm font-semibold text-indigo-100 mt-6 flex items-center"><span className="material-symbols-outlined text-sm mr-2 opacity-80">check_circle</span> Within budget targets</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden flex flex-col">
              <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                <h3 className="text-xl font-bold font-manrope text-slate-900">Recent Transactions</h3>
                <div className="flex items-center text-sm text-slate-500 font-medium cursor-pointer hover:text-slate-700">
                   Showing {transactions.length} entries <span className="material-symbols-outlined ml-1 text-lg">filter_list</span>
                </div>
              </div>
              <div className="flex-1 p-0 overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[600px]">
                  <thead>
                    <tr className="text-xs font-bold tracking-wider text-slate-400 uppercase bg-slate-50/50">
                      <th className="p-6 font-medium">Entity / Category</th>
                      <th className="p-6 font-medium">Status</th>
                      <th className="p-6 font-medium">Date</th>
                      <th className="p-6 font-medium text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {transactions.length > 0 ? transactions.map(t => (
                      <tr key={t.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="p-6">
                          <div className="flex items-center space-x-4">
                             <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${t.type === 'INCOME' ? 'bg-indigo-50 text-primary' : 'bg-slate-100 text-slate-600'}`}>
                                <span className="material-symbols-outlined">{t.type === 'INCOME' ? 'account_balance_wallet' : 'receipt_long'}</span>
                             </div>
                             <div>
                               <p className="font-bold text-slate-900 text-sm whitespace-nowrap">{t.title}</p>
                               <span className="inline-block mt-1 px-2 py-0.5 bg-slate-100 text-slate-500 rounded text-[10px] font-bold uppercase tracking-wider">{t.category?.name || 'Uncategorized'}</span>
                             </div>
                          </div>
                        </td>
                        <td className="p-6 whitespace-nowrap">
                           <span className={`flex items-center text-xs font-semibold ${t.status === 'CONFIRMED' ? 'text-primary' : 'text-slate-400'}`}><span className={`w-1.5 h-1.5 rounded-full mr-2 ${t.status === 'CONFIRMED' ? 'bg-primary' : 'bg-slate-400'}`}></span> {t.status === 'CONFIRMED' ? 'Completed' : 'Pending'}</span>
                        </td>
                        <td className="p-6 text-sm text-slate-500 font-medium whitespace-nowrap">
                           {format(new Date(t.date), 'MMM dd, yyyy', { locale: ptBR, timeZone: 'UTC' })}
                        </td>
                        <td className={`p-6 text-sm font-bold text-right whitespace-nowrap ${t.type === 'INCOME' ? 'text-primary' : 'text-slate-900'}`}>
                           {t.type === 'EXPENSE' ? '-' : '+'} R$ {t.amount.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan="4" className="p-8 text-center text-slate-500 font-medium">Nenhuma transação no período.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {transactions.length > 0 && (
                <div className="p-4 border-t border-slate-100 text-center">
                  <button className="text-sm font-bold text-primary hover:text-indigo-800 transition-colors">View All Transactional Data</button>
                </div>
              )}
            </div>

            <div className="lg:col-span-1 space-y-6">
               <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
                  <h3 className="text-lg font-bold font-manrope mb-6 text-slate-900">Spending Analysis</h3>
                  <div className="space-y-6">
                     <div>
                        <div className="flex justify-between text-sm font-semibold mb-2">
                           <span className="text-slate-600">Operational Costs</span>
                           <span className="text-slate-900">45%</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-primary w-[45%]"></div></div>
                     </div>
                     <div>
                        <div className="flex justify-between text-sm font-semibold mb-2">
                           <span className="text-slate-600">Lifestyle & Growth</span>
                           <span className="text-slate-900">32%</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-primary w-[32%]"></div></div>
                     </div>
                     <div>
                        <div className="flex justify-between text-sm font-semibold mb-2">
                           <span className="text-slate-600">Tax Reserves</span>
                           <span className="text-slate-900">23%</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-slate-300 w-[23%]"></div></div>
                     </div>
                  </div>
               </div>

               <div className={`p-8 rounded-xl border flex flex-col items-center text-center ${prediction.diff >= 0 ? 'bg-indigo-50 border-indigo-100' : 'bg-red-50 border-red-100'}`}>
                  <div className={`w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 ${prediction.diff >= 0 ? 'text-primary' : 'text-red-500'}`}>
                     <span className="material-symbols-outlined">auto_awesome</span>
                  </div>
                  <h3 className="text-lg font-bold font-manrope mb-2 text-slate-900">Smart Prediction</h3>
                  {prevTransactions.length === 0 ? (
                    <p className="text-sm font-medium text-slate-500 mb-8 leading-relaxed">
                      Sem dados do mês anterior para comparar. Continue registrando suas transações!
                    </p>
                  ) : prediction.diff >= 0 ? (
                    <p className="text-sm font-medium text-indigo-600 mb-8 leading-relaxed">
                      Seu saldo este mês é <strong className="text-primary font-bold">{formatCurrency(Math.abs(prediction.diff))}</strong> maior que o mês anterior. Continue assim!
                    </p>
                  ) : (
                    <p className="text-sm font-medium text-red-600 mb-8 leading-relaxed">
                      Seu saldo este mês é <strong className="text-red-700 font-bold">{formatCurrency(Math.abs(prediction.diff))}</strong> menor que o mês anterior. Revise seus gastos.
                    </p>
                  )}
                  <div className="w-full flex justify-between text-xs font-semibold text-slate-500 mb-2">
                    <span>Mês anterior</span>
                    <span>{formatCurrency(prediction.prevBalance)}</span>
                  </div>
                  <div className="w-full flex justify-between text-xs font-semibold text-slate-700">
                    <span>Este mês</span>
                    <span className={totalBalance >= 0 ? 'text-primary' : 'text-red-600'}>{formatCurrency(totalBalance)}</span>
                  </div>
               </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default MonthlyReport;